import React from 'react';
import { Box, Stack, Typography, GlobalStyles } from '@mui/material';

/**
 * Animated VerOps brand lockup: the real blue→green "V" mark (logo512.png) with
 * an entrance pop, a gentle continuous float, a pulsing brand glow, and a
 * periodic light-sweep shimmer — paired with the "VerOps" wordmark (V in brand
 * blue, "erOps" in ink). Animation is suppressed under prefers-reduced-motion.
 *
 * Props: size (mark px), hero (bigger float + stronger glow), wordmark (show text).
 */
const LOGO_SRC = `${process.env.PUBLIC_URL}/logo512.png`;
const BLUE = '#3b82f6';
const INK = '#e6edf3';

const BrandLogo = ({ size = 30, hero = false, wordmark = true }) => (
  <>
    <GlobalStyles styles={{
      '@keyframes verops-logo-in': {
        '0%': { opacity: 0, transform: 'translateY(8px) scale(0.6) rotate(-8deg)' },
        '100%': { opacity: 1, transform: 'translateY(0) scale(1) rotate(0)' },
      },
      '@keyframes verops-logo-float': {
        '0%,100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-6px)' },
      },
      '@keyframes verops-logo-glow': {
        '0%,100%': { filter: 'drop-shadow(0 0 6px rgba(37,99,235,0.45)) drop-shadow(0 4px 10px rgba(0,0,0,0.5))' },
        '50%': { filter: 'drop-shadow(0 0 16px rgba(16,185,129,0.55)) drop-shadow(0 4px 12px rgba(0,0,0,0.5))' },
      },
      '@keyframes verops-logo-sweep': {
        '0%': { transform: 'translateX(-130%) rotate(18deg)' },
        '100%': { transform: 'translateX(230%) rotate(18deg)' },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '.verops-mark, .verops-mark *': { animation: 'none !important' },
      },
    }} />
    <Stack direction="row" spacing={size > 40 ? 1.6 : 1} alignItems="center">
      <Box className="verops-mark" sx={{ position: 'relative', width: size, height: size, flexShrink: 0,
        animation: 'verops-logo-in .7s cubic-bezier(.2,.9,.25,1.3) both' }}>
        <Box sx={{ position: 'absolute', inset: 0,
          animation: `verops-logo-float ${hero ? 5 : 6}s ease-in-out infinite` }}>
          <Box component="img" src={LOGO_SRC} alt="VerOps logo"
            sx={{ width: '100%', height: '100%', display: 'block',
              animation: `verops-logo-glow ${hero ? 4 : 6}s ease-in-out infinite` }} />
          {/* light sweep */}
          <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden',
            WebkitMaskImage: `url(${LOGO_SRC})`, maskImage: `url(${LOGO_SRC})`,
            WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center', maskPosition: 'center' }}>
            <Box sx={{ position: 'absolute', top: '-40%', left: 0, width: '40%', height: '180%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent)',
              animation: 'verops-logo-sweep 4.5s ease-in-out 1.2s infinite' }} />
          </Box>
        </Box>
      </Box>
      {wordmark && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', letterSpacing: '-0.025em' }}>
          <Typography component="span" sx={{ fontWeight: 800, fontSize: size * 0.66, color: BLUE, lineHeight: 1 }}>V</Typography>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: size * 0.62, color: INK, lineHeight: 1 }}>erOps</Typography>
        </Box>
      )}
    </Stack>
  </>
);

export default BrandLogo;
